import { API_CONFIG } from "../config/api.config";

interface RequestOptions extends RequestInit {
  timeout?: number;
}

export class HttpError extends Error {
  status: number;
  statusText: string;
  data?: any;

  constructor(status: number, statusText: string, message: string, data?: any) {
    super(message);
    this.name = "HttpError";
    this.status = status;
    this.statusText = statusText;
    this.data = data;
  }
}

class HttpService {
  private defaultHeaders: HeadersInit = {
    "Content-Type": "application/json",
  };

  private isRefreshing = false;
  private refreshPromise: Promise<boolean> | null = null;

  private getAuthToken(): string | null {
    // Get token from localStorage or sessionStorage
    return localStorage.getItem("authToken");
  }

  private async attemptTokenRefresh(): Promise<boolean> {
    // If already refreshing, wait for that refresh to complete
    if (this.isRefreshing && this.refreshPromise) {
      return this.refreshPromise;
    }

    this.isRefreshing = true;
    this.refreshPromise = (async () => {
      try {
        const refreshToken = localStorage.getItem("refreshToken");
        if (!refreshToken) {
          return false;
        }

        const response = await fetch(`${API_CONFIG.baseURL}${API_CONFIG.apiPath}/auth/refresh`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ refreshToken }),
        });

        if (!response.ok) {
          return false;
        }

        const data = await response.json();
        if (data.accessToken && data.refreshToken) {
          localStorage.setItem("authToken", data.accessToken);
          localStorage.setItem("refreshToken", data.refreshToken);
          return true;
        }

        return false;
      } catch (error) {
        console.error("Token refresh failed:", error);
        return false;
      } finally {
        this.isRefreshing = false;
        this.refreshPromise = null;
      }
    })();

    return this.refreshPromise;
  }

  private async request<T>(url: string, options: RequestOptions = {}): Promise<T> {
    const { timeout = API_CONFIG.timeout, ...fetchOptions } = options;

    // Prepend baseURL if url is relative
    const fullUrl = url.startsWith("http") ? url : `${API_CONFIG.baseURL}${url}`;

    // Add auth token if available, but don't override explicitly passed Authorization header
    const token = this.getAuthToken();
    const hasExplicitAuthHeader = options.headers && "Authorization" in options.headers;

    // Don't add default Content-Type header for FormData requests
    const isFormData = fetchOptions.body instanceof FormData;
    const headers = {
      ...(isFormData ? {} : this.defaultHeaders),
      ...(token && !hasExplicitAuthHeader ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    };

    // Create abort controller for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const response = await fetch(fullUrl, {
        ...fetchOptions,
        headers,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      // Handle non-2xx responses
      if (!response.ok) {
        // Special handling for 401 - try to refresh token first
        if (
          response.status === 401 &&
          !url.includes("/auth/refresh") &&
          !url.includes("/auth/login")
        ) {
          const refreshed = await this.attemptTokenRefresh();
          if (refreshed) {
            // Retry the original request with new token
            const retryToken = this.getAuthToken();
            const retryHeaders = {
              ...(isFormData ? {} : this.defaultHeaders),
              ...(retryToken ? { Authorization: `Bearer ${retryToken}` } : {}),
              ...options.headers,
            };

            const retryResponse = await fetch(fullUrl, {
              ...fetchOptions,
              headers: retryHeaders,
            });

            if (retryResponse.ok) {
              const contentLength = retryResponse.headers.get("content-length");
              if (contentLength === "0" || retryResponse.status === 204) {
                return {} as T;
              }

              const contentType = retryResponse.headers.get("content-type");
              if (contentType && contentType.includes("text/html")) {
                const htmlContent = await retryResponse.text();
                throw new HttpError(
                  retryResponse.status,
                  retryResponse.statusText,
                  `Server returned HTML instead of JSON. This usually indicates a server error or misconfiguration.`,
                  { responseText: htmlContent.substring(0, 200) }
                );
              } else if (contentType && contentType.includes("text/plain")) {
                const textContent = await retryResponse.text();
                return { success: true, message: textContent } as T;
              }

              return await retryResponse.json();
            }

            // Retry failed — throw error based on retry response, NOT original 401.
            // This prevents incorrect redirect to /login for transient errors (e.g. 503).
            const retryError = await this.handleErrorResponse(retryResponse);
            throw retryError;
          }
        }

        const error = await this.handleErrorResponse(response);
        throw error;
      }

      // Handle empty responses
      const contentLength = response.headers.get("content-length");
      if (contentLength === "0" || response.status === 204) {
        return {} as T;
      }

      // Check content type and parse accordingly
      const contentType = response.headers.get("content-type");

      // If HTML is returned, it's likely an error page - handle this first
      if (contentType && contentType.includes("text/html")) {
        const htmlContent = await response.text();
        throw new HttpError(
          response.status,
          response.statusText,
          `Server returned HTML instead of JSON. This usually indicates a server error or misconfiguration.`,
          { responseText: htmlContent.substring(0, 200) }
        );
      } else if (contentType && contentType.includes("text/plain")) {
        // For text/plain responses, return a success indicator if status is 2xx
        const textContent = await response.text();
        return { success: true, message: textContent } as T;
      }

      // For JSON or unknown content types, try to parse as JSON
      try {
        return await response.json();
      } catch (parseError) {
        // If JSON parsing fails, provide informative error
        // Note: We can't read response.text() here because body is already consumed
        throw new HttpError(
          response.status,
          response.statusText,
          `Failed to parse JSON response. The server may have returned invalid JSON or an error page.`,
          { originalError: parseError }
        );
      }
    } catch (error) {
      clearTimeout(timeoutId);

      if (error instanceof Error) {
        if (error.name === "AbortError") {
          throw new HttpError(408, "Request Timeout", "The request took too long to complete", {
            originalError: error,
          });
        }

        // Network errors (like CORS, network offline, etc.)
        if (error.name === "TypeError" && error.message.includes("Failed to fetch")) {
          throw new HttpError(
            0,
            "Network Error",
            "Unable to connect to the server. Please check your internet connection.",
            { originalError: error }
          );
        }

        // If it's already an HttpError, just rethrow it
        if (error instanceof HttpError) {
          throw error;
        }
      }

      // For any other type of error, wrap it in an HttpError
      throw new HttpError(
        0,
        "Unknown Error",
        error instanceof Error ? error.message : "An unknown error occurred",
        { originalError: error }
      );
    }
  }

  private async handleErrorResponse(response: Response): Promise<HttpError> {
    let message = `HTTP ${response.status}: ${response.statusText}`;
    let errorData: any = null;
    const passwordExpired = response.headers.get("X-Password-Expired") === "true";

    try {
      errorData = await response.json();
      message = errorData.message || errorData.error || message;
    } catch {
      // If response is not JSON, use default message
    }

    // Handle specific status codes
    switch (response.status) {
      case 401: {
        // Check current path to determine if we should redirect or just throw error
        const currentPath = window.location.pathname;
        const isOnLoginPage = currentPath === "/login";
        const isOnProfilePage = currentPath === "/profile";

        // Don't redirect if on login page (MFA errors) or profile page (phone verification errors)
        if (isOnLoginPage || isOnProfilePage) {
          break;
        }

        // For all other pages, clear auth and redirect to login
        localStorage.removeItem("authToken");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("currentUser");
        localStorage.removeItem("userProfile");
        localStorage.removeItem("organisationData");
        window.location.href = "/login";
        break;
      }
      case 403:
        // Keep the server's message (e.g. "Password update required") unless it's generic
        if (!errorData?.message && !passwordExpired) {
          message = "You do not have permission to perform this action";
        }
        break;
      case 404:
        if (!errorData?.message) {
          message = "The requested resource was not found";
        }
        break;
      case 500:
        if (!errorData?.message) {
          message = "An internal server error occurred";
        }
        break;
      case 502:
        message = "Bad gateway error - the server is temporarily unavailable";
        break;
      case 503:
        message = "Service unavailable - the server is temporarily overloaded";
        break;
      case 504:
        message = "Gateway timeout - the server took too long to respond";
        break;
    }

    return new HttpError(response.status, response.statusText, message, errorData);
  }

  // HTTP Methods
  async get<T>(url: string, options?: RequestOptions): Promise<T> {
    return this.request<T>(url, {
      ...options,
      method: "GET",
    });
  }

  async post<T>(url: string, data?: any, options?: RequestOptions): Promise<T> {
    return this.request<T>(url, {
      ...options,
      method: "POST",
      body: data instanceof FormData ? data : data ? JSON.stringify(data) : undefined,
    });
  }

  async put<T>(url: string, data?: any, options?: RequestOptions): Promise<T> {
    return this.request<T>(url, {
      ...options,
      method: "PUT",
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async patch<T>(url: string, data?: any, options?: RequestOptions): Promise<T> {
    return this.request<T>(url, {
      ...options,
      method: "PATCH",
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T>(url: string, options?: RequestOptions): Promise<T> {
    return this.request<T>(url, {
      ...options,
      method: "DELETE",
    });
  }

  // Set auth token
  setAuthToken(token: string): void {
    localStorage.setItem("authToken", token);
  }

  // Clear auth token
  clearAuthToken(): void {
    localStorage.removeItem("authToken");
  }
}

export const httpService = new HttpService();
